// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */

import compact from 'lodash/compact';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import flatten from 'lodash/flatten';

import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ListOperator, LogicalOperator} from 'common/widgets/property-filter-menu/models/types';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {PropertyType, ResourceType} from 'common/report/constants';
import {AUTOMATED_CUSTOM_PROPERTY_TRACKING} from 'common/constants';
import type {CohortStore} from 'common/report/stores/cohorts';
import {pluralize} from 'common/util/string';
import type {DataGroup} from 'common/widgets/data-group-selector/types';
import type {Segmentation} from 'common/types/reports/query';

import {CohortSegment, isInlineCohortSegment, SavedCohortSegment} from '../segment-menu/types';
import * as PropertiesMenu from '../properties-menu/types';
import {BookmarkCohortFilter, CohortFilterTree, CohortFilterTreeChild} from './types';

function generateCohortFilterTree(filter: PropertyFilter) {
  let cohorts = filter.filterValue as CohortSegment[];
  const negated = filter.filterOperator === ListOperator.DoesNotContain;

  cohorts = cohorts.map((value) => {
    return {
      [ResourceType.Cohort]: {
        ...value.cohort,
        isInherited: filter.isInherited,
      },
    } as CohortSegment;
  });

  return generateCohortFilterSubTree(cohorts, negated);
}

function generateCohortFilterSubTree(cohorts: CohortSegment[], negated: boolean): CohortFilterTree {
  if (!cohorts.length) {
    return null;
  }

  const cohortFilter = getFilterByCohort(cohorts[0], negated);
  const remainingCohorts = cohorts.slice(1);

  // eslint-disable-next-line sonarjs/prefer-immediate-return
  const filterByCohort = {
    operator: LogicalOperator.Or,
    children: compact([
      cohortFilter,
      remainingCohorts.length === 1
        ? getFilterByCohort(remainingCohorts[0], negated)
        : generateCohortFilterSubTree(remainingCohorts, negated),
    ]),
  };

  // @ts-ignore - TODO: Type '{ operator: LogicalOperator; children: (CohortFilterTree | { cohort: { id: number; name: string; negated: boolean; }; })[]; }' is not assignable to type 'CohortFilterTree'.
  return filterByCohort;
}

export function getFilterByCohorts(filters: PropertyFilter[]): CohortFilterTree {
  if (filters.length === 1) {
    return generateCohortFilterTree(filters[0]);
  }

  return {
    operator: LogicalOperator.And,
    children: [generateCohortFilterTree(filters[0]), getFilterByCohorts(filters.slice(1))],
  };
}

function getFilterByCohort(filter: CohortSegment, negated = false) {
  if ((filter as SavedCohortSegment).cohort.id) {
    const {cohort} = filter as SavedCohortSegment;
    return {
      cohort: {
        id: cohort.id,
        isInherited: filter.cohort.isInherited,
        name: cohort.name,
        negated,
        ...(cohort.data_group_id ? {data_group_id: cohort.data_group_id} : {}),
      },
    };
  }

  // Inline cohort
  return {
    cohort: {
      ...filter.cohort,
      isInherited: filter.cohort.isInherited,
      negated,
    },
  };
}

function isBookmarkCohortFilter(filter: BookmarkCohortFilter | CohortFilterTree): filter is BookmarkCohortFilter {
  return filter && Object.prototype.hasOwnProperty.call(filter, `cohort_id`);
}

function isBookmarkCohortTree(filter: CohortFilterTreeChild): filter is CohortFilterTree {
  return filter && Object.prototype.hasOwnProperty.call(filter, `operator`);
}

/**
 * Parses filter by cohorts
 */
// TODO opslead
export function bookmarkFilterByCohortToCohortFilters(
  filterByCohort: string | CohortFilterTree | BookmarkCohortFilter | UnknownObj,
): PropertyFilter[] {
  const filters = [];
  const cohortFilter: CohortFilterTree | BookmarkCohortFilter = isString(filterByCohort)
    ? JSON.parse(filterByCohort || null)
    : filterByCohort;
  const getCohortFilter = (cohort, negated = null): CohortSegment => {
    if (cohort.raw_cohort) {
      return {
        cohort: {
          raw_cohort: cohort.raw_cohort,
          name: cohort.name,
          isInherited: cohort.raw_cohort.isInherited ?? false,
          negated: negated !== null ? negated : cohort.negated,
        },
      };
    }

    return {
      cohort: {
        id: cohort.id || cohort.cohort_id,
        name: cohort.name,
        isInherited: cohort.isInherited ?? false,
        negated: negated !== null ? negated : cohort.negated,
        ...(cohort.data_group_id ? {data_group_id: cohort.data_group_id} : {}),
      },
    };
  };

  if (isEmpty(cohortFilter)) {
    return filters;
  } else if (isBookmarkCohortFilter(cohortFilter)) {
    const cohortList = [getCohortFilter(cohortFilter)];
    // handle single cohort case
    return [
      new PropertyFilter({
        isInherited: cohortList[0]?.cohort?.isInherited ?? false,
        filterOperator: cohortFilter.negated ? ListOperator.DoesNotContain : ListOperator.Contains,
        filterValue: cohortList,
        propertyType: PropertyType.List,
        resourceType: ResourceType.Cohort,
      }),
    ];
  }
  return collectFilterGroups(cohortFilter, true) as PropertyFilter[];

  /**
   * Traverse the cohort tree to produce distinct groups of cohort filters.
   *
   * Assumption: the high level filter groups are joined by ANDs at the top,
   * and ANDs always come above ORs in the cohort filter tree.
   */
  function collectFilterGroups(node: CohortFilterTree | CohortFilterTreeChild, isRoot = false) {
    if (isBookmarkCohortTree(node)) {
      const {operator} = node;
      let filterValues;
      if (operator === LogicalOperator.And) {
        // AND nodes produce distinct groups of CohortSegments (array of arrays)
        // concat the children as they come back
        filterValues = node.children
          .map(function mapChildrenOfAndNode(child) {
            return collectFilterGroups(child);
          })
          .reduce((arr, child) => arr.concat(child), []);
      } else if (operator === LogicalOperator.Or) {
        // OR nodes produce one group of CohortSegments (single array)
        // wrap in array on the way up so we know it's a distinct group:  [[v1, v2]]
        filterValues = [
          node.children.flatMap(function mapChildrenOfOrNode(child) {
            // response will come back wrapped in array, so unwrap with flatten
            return flatten(collectFilterGroups(child));
          }),
        ];
      } else {
        throw new Error(`Invalid cohort operator: ${operator}`);
      }

      if (isRoot) {
        return filterValues.map(toPropertyFilter);
      }
      return filterValues;
    }
    return [getCohortFilter(node.cohort)];
  }

  function toPropertyFilter(maybeCohortList: CohortSegment | CohortSegment[]) {
    let cohortList: CohortSegment[];
    if (!Array.isArray(maybeCohortList)) {
      cohortList = [maybeCohortList];
    } else {
      cohortList = maybeCohortList;
    }
    const shouldNegate = cohortList[0].cohort.negated;
    return new PropertyFilter({
      isInherited: cohortList[0]?.cohort?.isInherited ?? false,
      filterOperator: shouldNegate ? ListOperator.DoesNotContain : ListOperator.Contains,
      filterValue: cohortList.map((cohort) => {
        if (shouldNegate) {
          cohort.cohort.negated = false;
        }
        return cohort;
      }),
      propertyType: PropertyType.List,
      resourceType: ResourceType.Cohort,
    });
  }
}

/**
 * Generates tracking props for a given PropertyFilter
 */
export function generateFilterTrackingProps(item: PropertyFilter, extraProps?: Obj) {
  const isCohort = item.resourceType === ResourceType.Cohort;
  const cohortNames =
    isCohort &&
    Array.isArray(item.filterValue) &&
    (item.filterValue as CohortSegment[]).map(
      (filter) => filter.cohort.name ?? (filter as SavedCohortSegment)?.cohort?.id,
    );

  if (item.customProperty?.pre_populated_entity_id) {
    extraProps = {
      ...extraProps,
      [AUTOMATED_CUSTOM_PROPERTY_TRACKING]: item.customProperty?.pre_populated_entity_id,
    };
  }

  return {
    'Property name': item.propertyName ?? null,
    'Property data type': item.propertyType,
    'Property default type': item.propertyDefaultType ?? null,
    'Property join type': item.joinPropertyType ?? null,
    'Property scd event': item.scdEvent ?? null,
    'Property scd options': item.scdOptions ?? null,
    'Property list quantifier': item.listQuantifier ?? null,
    'Resource type': item.resourceType,
    'Filter operator': item.filterOperator,
    'Filter value': isCohort ? cohortNames : item.filterValue,
    Behavior: item.behavior ?? null,
    'Behavior Property': item.behavior?.property ?? null,
    [commonTrackingEventPropertyName.usedLookupTableProperties]: !!item.dataGroupId && isArray(item.propertyName),
    [commonTrackingEventPropertyName.usedEventCustomProperty]:
      Boolean(item.customPropertyId || item.customProperty) && item.resourceType === ResourceType.Event,
    [commonTrackingEventPropertyName.usedUserCustomProperty]:
      Boolean(item.customPropertyId || item.customProperty) && item.resourceType === ResourceType.User,
    [commonTrackingEventPropertyName.dataGroupId]: item.dataGroupId ?? null,
    ...extraProps,
  };
}

/**
 * Obtains a property item from a selected property filter
 */
export function transformFilterToPropertyItem(selection: Partial<PropertyFilter>): PropertiesMenu.Selection {
  const selectionResourceType = selection && selection.resourceType;

  switch (selectionResourceType) {
    case ResourceType.Cohort: {
      const filterValue = selection.filterValue as CohortSegment[];
      return filterValue?.map((entry) => {
        if (isInlineCohortSegment(entry)) {
          return {
            resourceType: ResourceType.Cohort,
            label: entry.cohort.name,
            value: entry,
          };
        }
        return {
          resourceType: ResourceType.Cohort,
          label: entry.cohort.name,
          value: entry.cohort.id,
        };
      });
    }
    case ResourceType.Other:
    case ResourceType.User:
    case ResourceType.Event: {
      const propertyType =
        selection.propertyType || (selection.customProperty && selection.customProperty.propertyType);
      return {
        ...(selection.customPropertyId ? {customPropertyId: selection.customPropertyId} : null),
        ...(selection.customProperty ? {customProperty: selection.customProperty} : null),
        ...(selection.joinPropertyType ? {joinPropertyType: selection.joinPropertyType} : null),
        ...(selection.scdEvent ? {scdEvent: selection.scdEvent} : null),
        ...(selection.tempDataGroupId ? {tempDataGroupId: selection.tempDataGroupId} : null),
        ...(selection.behavior ? {behavior: selection.behavior} : null),
        dataGroupId: selection.dataGroupId,
        propertyDefaultType: selection.propertyDefaultType || propertyType,
        ...(selection.propertyObjectKey
          ? {
              propertyObjectKey: selection.propertyObjectKey,
            }
          : {}),
        resourceType: selectionResourceType,
        type: propertyType,
        value: selection.propertyName,
      };
    }
    default:
      return null;
  }
}

const LOADING_NOTICE = `Loading...`;

// Terminology may not be/remain the same as Cohort operators so we remap
// the labels
const COHORT_FILTER_OPERATOR_LABELS = {
  [ListOperator.Contains]: `in`,
  [ListOperator.DoesNotContain]: `not in`,
};

export function getCohortGroupLabel(dataGroups: DataGroup[], dataGroupId: string) {
  if (!dataGroupId) {
    return `Users`;
  }
  const dataGroup = dataGroups.find((group) => group.groupID === dataGroupId);
  return dataGroup ? `${dataGroup.displayName}(s)` : `Groups`;
}

export function getCohortFilterLabelParts(
  filter: Segmentation,
  cohortsStore: CohortStore,
  dataGroups: DataGroup[],
  dataGroupId: string,
) {
  const {filterValue, filterOperator} = filter;
  const cohortCount = Array.isArray(filterValue) ? filterValue.length : 0;
  const groupLabel = getCohortGroupLabel(dataGroups, dataGroupId);

  const firstCohort = filter.filterValue[0].cohort;
  let cohortName = firstCohort.name;

  // for saved cohorts, prefer using their canonical name from the cohort store
  if (Number.isInteger(firstCohort.id)) {
    const cohort = cohortsStore.value[firstCohort.id];
    cohortName = cohort?.name || firstCohort.name || LOADING_NOTICE;
  }

  const suffix = [
    cohortName,
    cohortCount > 1 ? ` or ${cohortCount - 1} ${pluralize(`other`, cohortCount - 1)}` : ``,
  ].join(``);

  return {
    group: groupLabel,
    operator: COHORT_FILTER_OPERATOR_LABELS[filterOperator] || filterOperator,
    value: suffix,
  };
}
